import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { parseQuery } from '@common/typescript/utils/url';
import {
	defaultTransformFiltersBeforeHandleUrl, ItemsProvider, useItemsProviderContext,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import Pagination from '@common/react/components/base/Pagination/Pagination';
import useRequest from '@common/react/hooks/useRequest';
import BaseModalContextProvider from '@common/react/components/base/BaseModalContextProvider/BaseModalContextProvider';

import CaseModalInner from '@commonTuna/react/components/UI/CaseModalInner/CaseModalInner';
import { Case } from '@commonTuna/react/objects/BaseCase';

import BeforeAfterItem from '@app/components/Pages/BeforeAfterList/BeforeAfterItem';
import BeforeAfterFilters, { initialFilterState } from '@app/components/Pages/BeforeAfterList/BeforeAfterFilters';
import { t } from '@app/components/Utils';

import '@app/scss/pages/beforeafter.scss';

interface BeforeAfterMainProps {
	initialValues: any;
}

interface ModalState {
	visible: boolean;
	caseId: number | null;
	index: null | number;
}

const initialModalState: ModalState = {
	visible: false,
	caseId: null,
	index: null,
};

const selectProps = {
	theme: (theme) => ({
		...theme,
		colors: {
			...theme.colors,
			primary25: 'rgba(255, 255, 255, 0.3)',
			primary: 'rgba(178,132,15,0.3)',
		},
	}),
	styles: {
		option: (base, state) => ({
			...base,
			color: state.isSelected ? '#fff !important' : 'inherit',
			backgroundColor: state.isSelected ? '#eee' : 'inherit',
		}),
	},
};

export const BeforeAfterMain = (props: BeforeAfterMainProps) => {
	const context = useItemsProviderContext<Case>();

	const {
		state: {
			items, pagination, loading, filters,
		},
		actions: { handleChange },
	} = context;
	const ref = React.useRef(-1);
	const [firstOpen, setFirstOpen] = React.useState(false);
	const [showFilter, setVisibilityFilter] = React.useState(false);
	const [modalState, setModalState] = React.useState(initialModalState);
	const { renderModal } = useModal();
	const request = useRequest();

	const closeDialog = () => {
		setModalState((prevState) => ({
			...prevState,
			visible: false,
			index: null,
		}));
	};

	const handleCaseClick = (caseId: number) => {
		setVisibilityFilter(false);
		setModalState((prevState) => ({
			...prevState,
			visible: true,
			caseId,
		}));
		setFirstOpen(true);
	};

	const findCurrCaseIndex = () => {
		if (!items) return;
		const currCase = items.filter((c) => c.id === modalState.caseId)[0];
		const index = items.indexOf(currCase);
		return index >= 0 ? index : undefined;
	};

	const onArrowClick = (number = 1) => {
		const currIndex = findCurrCaseIndex();
		if (!items) return;

		setModalState((prev) => {
			const newCurrentIndex = (currIndex || 0) + (pagination.current - 1) * pagination.pageSize + number;
			const newIndex = currIndex !== undefined && currIndex > -1
				? newCurrentIndex < 0 ? pagination.total - 1
					: newCurrentIndex >= pagination.total ? 0 : newCurrentIndex
				: prev.index && prev.index + number === pagination.total ? 0
					: prev.index && prev.index + number === -1 ? pagination.total - 1
						: (prev.index || 0) + number;
			ref.current = newIndex;
			if (currIndex === undefined || currIndex === 0 || currIndex + 1 === items.length) {
				request('caseViewListRemote', {
					...filters,
					pageSize: 1,
					page: newIndex,
					offset: newIndex,
				}).then((res: any) => {
					if (ref.current === newIndex) {
						setModalState((prev) => ({
							...prev,
							caseId: res.list[0].id,
						}));
					}
				});
			}

			return ({
				...prev,
				caseId: currIndex !== undefined && currIndex > 0 && currIndex + 1 < items.length ? items[currIndex + number].id : -1,
				index: newIndex,
			});
		});
	};

	const handleLeftClick = () => {
		onArrowClick(-1);
	};

	const handleRightClick = () => {
		onArrowClick();
	};

	return <>
		<div className="clearfix">
			<button
				type="button"
				className="general-form-button before-after__toggle-filters ml10"
				onClick={() => setVisibilityFilter(true)}
			>
				<i className="fa fa-sliders" />
			</button>
		</div>
		<div className="clearfix is-relative" style={{ paddingTop: 30 }}>
			<div className={`before-after__filters-wrapper ${showFilter ? 'before-after__filters-wrapper_show' : ''}`}>
				<i className="fa fa-times before-after__filters-wrapper-close" onClick={() => setVisibilityFilter(false)} />
				<BeforeAfterFilters
					initialValues={props.initialValues}
				/>
			</div>
			<div className="before-after__cases-wrapper">
				{items.length > 0 ? items.map((item: Case) => <BeforeAfterItem
					item={item}
					onCaseClick={handleCaseClick}
					key={`${item.id}`}
				/>)
					: <h3 className="text-center">Items Not Found</h3>}
				{loading && items.length > 0 ? <Loader className="before-after__cases-wrapper_loading" /> : null}
				{pagination.total > 10
					? (
						<Pagination
							key="pagination"
							className="dark-pagination"
							current={pagination.current}
							pageSize={pagination.pageSize}
							total={pagination.total ? pagination.total : 0}
							onChange={(page) => handleChange({ page })}
							selectProps={selectProps}
						/>
					) : null
				}
			</div>
		</div>
		{renderModal(modalState.visible, closeDialog, {
			afterClose: closeDialog,
			className: 'dark-modal case-modal',
			style: { width: '100%' },
			children: <CaseModalInner
				lang="En"
				t={t}
				caseId={modalState.caseId}
				leftClick={handleLeftClick}
				rightClick={handleRightClick}
			/>,
		})}
	</>;
};

const BeforeAfterList = () => {
	const location = useLocation();

	const search = parseQuery(location.search);
	const defCurrent = +search.page || 1;

	return <div className="before-after container inner-page">
		<ServerPageProvider
			inner={(serverPage) =>
				<ItemsProvider
					rows={serverPage?.items}
					filters={{ ...initialFilterState }}
					pagination={{
						current: defCurrent, pageSize: 10,
					}}
					loadRequest="caseViewListRemote"
					withHandleUrl
					pageInSearch
					transformFiltersBeforeHandleUrl={(filters) => ({
						...defaultTransformFiltersBeforeHandleUrl(filters),
						page: (filters as any).page || 1,
					})}
				>
					<BaseModalContextProvider>
						<BeforeAfterMain
							initialValues={serverPage?.filterProps}
						/>
					</BaseModalContextProvider>
				</ItemsProvider>
			}
		/>
	</div>;
};

export default BeforeAfterList;
